import { Dispatch, useState } from "react";
import { Action } from "../../../Bundles/Types";
import { CartConfigInterface, SingleTriggeredRewardTier } from "../../CartTypes";
import styles from "./CartFormCards.module.css";
import ConfigureTriggerRewardModal from "./ConfigureTriggerRewardModal";
import { FormLayout, EmptyState, Link, Card, Text, Button, InlineStack, BlockStack } from "@shopify/polaris";
import { DeleteIcon, EditIcon } from "@shopify/polaris-icons";

const TriggeredRewardsForm = ({
    fields,
    dispatchCartConfig,
}: {
    fields: any;
    dispatchCartConfig: Dispatch<Action<CartConfigInterface>>;
}) => {
    const { triggered_rewards } = fields;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);

    // Removed handleToggleFeature as it was unused and the toggle logic is handled in CartDrawerAdmin

    const handleAddOrUpdateTier = (tier: SingleTriggeredRewardTier) => {
        const updatedTiers = [...triggered_rewards.tiers.value];
        if (editingIndex !== null) {
            updatedTiers[editingIndex] = tier;
        } else {
            updatedTiers.push(tier);
        }

        triggered_rewards.tiers.onChange(updatedTiers);
        dispatchCartConfig({
            payload: { tiers: updatedTiers },
            isComplex: true,
            key: "triggered_rewards",
        });
        setIsModalOpen(false);
        setEditingIndex(null);
    };

    const handleDeleteTier = (index: number) => {
        const updatedTiers = triggered_rewards.tiers.value.filter((_: any, i: number) => i !== index);
        triggered_rewards.tiers.onChange(updatedTiers);
        dispatchCartConfig({
            payload: { tiers: updatedTiers },
            isComplex: true,
            key: "triggered_rewards",
        });
    };

    const openEditModal = (index: number) => {
        setEditingIndex(index);
        setIsModalOpen(true);
    };

    return (
        <>
            <div className={styles.designCardWrapper}>
                <div className={styles.cardLabel}>Reward rules</div>
                <FormLayout>
                    {!triggered_rewards.tiers.value.length ? (
                        <EmptyState
                            heading="Create your first triggered reward"
                            action={{ content: " + Create Triggered Reward", onAction: () => setIsModalOpen(true) }}
                            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                        >
                            <p>Example: Add a free gift when a customer adds any product from a selected collection.</p>
                        </EmptyState>
                    ) : (
                        <BlockStack gap="400">
                            {triggered_rewards.tiers.value.map((tier: SingleTriggeredRewardTier, index: number) => {
                                const extraGifts = tier.free_gifts.length - 1;
                                const isProduct = tier.trigger_type === "product";
                                const items = isProduct ? tier.products : tier.collections;
                                const extraCount = items.length - 1;
                                return (
                                    <Card key={index}>
                                        <BlockStack gap="200">
                                            <InlineStack align="space-between" blockAlign="center">
                                                <InlineStack gap="200" blockAlign="center">
                                                    <Text as="h2" variant="headingSm">
                                                        Rule {index + 1}
                                                    </Text>
                                                </InlineStack>

                                                <InlineStack gap="200">
                                                    <Button
                                                        icon={EditIcon}
                                                        variant="tertiary"
                                                        onClick={() => openEditModal(index)}
                                                    />
                                                    <Button
                                                        icon={DeleteIcon}
                                                        variant="tertiary"
                                                        tone="critical"
                                                        onClick={() => handleDeleteTier(index)}
                                                    />
                                                </InlineStack>
                                            </InlineStack>

                                            <Text as="p" tone="subdued">
                                                {tier.trigger_type.charAt(0).toUpperCase() + tier.trigger_type.slice(1)}
                                                :&nbsp;<strong>{items[0]?.title}</strong>
                                                {extraCount > 0 && (
                                                    <>,&nbsp;
                                                    <Link onClick={() => openEditModal(index)}>+{extraCount} more</Link>
                                                    </>
                                                )}
                                            </Text>

                                            <Text as="p" tone="subdued">
                                                Free product added: <strong>{tier.free_gifts[0]?.title}</strong>
                                                {extraGifts > 0 && (
                                                   <>,&nbsp;
                                                    <Link onClick={() => openEditModal(index)}>+{extraGifts} more</Link>
                                                   </>
                                                )}
                                            </Text>
                                        </BlockStack>
                                    </Card>
                                );
                            })}

                            <div style={{ display: "flex", justifyContent: "center", marginTop: "10px" }}>
                                <Button onClick={() => setIsModalOpen(true)} variant="secondary">
                                    Add Another Rule
                                </Button>
                            </div>
                        </BlockStack>
                    )}
                </FormLayout>
            </div>

            {isModalOpen && (
                <ConfigureTriggerRewardModal
                    isOpen={isModalOpen}
                    onClose={() => {
                        setIsModalOpen(false);
                        setEditingIndex(null);
                    }}
                    onSave={handleAddOrUpdateTier}
                    initialData={editingIndex !== null ? triggered_rewards.tiers.value[editingIndex] : null}
                />
            )}
        </>
    );
};

export default TriggeredRewardsForm;
